import React from 'react';
import { useTranslation } from 'react-i18next';
import { useAppDispatch, useAppSelector } from '@common-reducers/hooks/store.hook';
import { dunningSelector } from '@common-reducers/UserSelectors';
import { selectOnlineEngines } from '@common-reducers/ConnectedEnginesSlice';
import { dismissNudge, selectNudgeDismissed } from '@common-reducers/nudge-slice';
import {
  EXTENSION_LISTING_URL,
  EXTENSION_UPDATE_DISMISS_KEY,
  shouldShowExtensionUpdate,
} from './extension-update-logic';

/**
 * Web-app banner asking the user to update an outdated Blueticks extension, driven by the
 * extension's live presence (version). Dunning wins. Dismissible for the session.
 */
const ExtensionUpdateBanner: React.FC = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const engines = useAppSelector(selectOnlineEngines);
  const { isDunning } = useAppSelector(dunningSelector);
  const dismissed = useAppSelector((s) => selectNudgeDismissed(s, EXTENSION_UPDATE_DISMISS_KEY));

  if (!shouldShowExtensionUpdate(engines, dismissed, !!isDunning)) return null;

  return (
    <div
      role="status"
      style={{
        width: '100%',
        backgroundColor: '#2563eb',
        color: '#fff',
        padding: '10px 16px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '12px',
        fontSize: '14px',
        fontWeight: 500,
        boxSizing: 'border-box',
      }}
    >
      <span>
        <strong>{t('extensionUpdateBanner.headline')}</strong>
        <span style={{ marginInlineStart: 8, fontWeight: 400 }}>{t('extensionUpdateBanner.body')}</span>
      </span>
      <button
        onClick={() => window.open(EXTENSION_LISTING_URL, '_blank', 'noopener,noreferrer')}
        style={{
          backgroundColor: '#fff',
          color: '#2563eb',
          border: 'none',
          borderRadius: '4px',
          padding: '6px 16px',
          fontWeight: 600,
          cursor: 'pointer',
          fontSize: '13px',
          whiteSpace: 'nowrap',
        }}
      >
        {t('extensionUpdateBanner.cta')}
      </button>
      <button
        aria-label={t('extensionUpdateBanner.dismiss')}
        onClick={() => dispatch(dismissNudge(EXTENSION_UPDATE_DISMISS_KEY))}
        style={{ background: 'transparent', border: 'none', color: '#fff', cursor: 'pointer', fontSize: '18px', lineHeight: 1, padding: '0 4px' }}
      >
        ×
      </button>
    </div>
  );
};

export default ExtensionUpdateBanner;
